import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { Icon } from "@repo/components/src/ui/icon";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { MailCheck } from "lucide-react-native";
import { useState } from "react";
import ChangeEmailForm from "./ChangeEmailForm";
import ResendLinkButton, { RESEND_COOLDOWN_MS } from "./ResendLinkButton";

type Props = {
  email: string;
  /** When the backend sends another link (epoch ms), if it already told: a page
   *  reloaded after the sign-up. Otherwise a link was just sent. */
  initialResendAt?: number;
  onEmailChanged: (email: string) => void;
};

/**
 * «Revisa tu correo». The account exists and holds the campaign; opening the emailed
 * link verifies the address, publishes the campaign and opens it. Until then the link
 * can be sent again (`ResendLinkButton`) or to another address, for a typo
 * (`ChangeEmailForm`).
 */
const CheckEmail = ({ email, initialResendAt, onEmailChanged }: Props) => {
  const [changing, setChanging] = useState(false);
  // Without the server's word, a link was just sent: to this address on mount, to
  // the new one after a change.
  const [resendAt, setResendAt] = useState(
    () => initialResendAt ?? Date.now() + RESEND_COOLDOWN_MS,
  );
  const restartCooldown = () => setResendAt(Date.now() + RESEND_COOLDOWN_MS);

  return (
    <VStack space="lg" className="w-full items-center">
      <Box className="h-14 w-14 items-center justify-center rounded-full bg-brand-50">
        <Icon as={MailCheck} size="xl" className="text-brand-500" />
      </Box>
      <VStack space="xs" className="items-center">
        <Text className="text-center text-xl font-bold text-typography-900">
          Revisa tu correo
        </Text>
        <Text className="text-center text-sm text-typography-600">
          {`Enviamos un enlace a ${email}. Tu campaña se publica cuando confirmes tu correo.`}
        </Text>
        <Text className="text-center text-xs text-typography-500">
          ¿No lo ves? Revisa tu carpeta de spam o promociones.
        </Text>
      </VStack>
      {changing ? (
        <ChangeEmailForm
          onCancel={() => setChanging(false)}
          onChanged={(newEmail) => {
            setChanging(false);
            restartCooldown();
            onEmailChanged(newEmail);
          }}
        />
      ) : (
        <VStack space="sm" className="w-full items-center">
          <ResendLinkButton
            email={email}
            resendAt={resendAt}
            onSent={restartCooldown}
          />
          <Button
            action="muted"
            variant="link"
            size="sm"
            onPress={() => setChanging(true)}
          >
            <ButtonText>¿No es tu correo? Cambiarlo</ButtonText>
          </Button>
        </VStack>
      )}
    </VStack>
  );
};

export default CheckEmail;
